import {Inject, Injectable} from '@angular/core';
import * as _ from 'lodash';
import {
    ColumnsOccurrence,
    ICanvasJSAxisIndex, ICanvasJSAxisOptions,
    ICanvasJSChartOptions, ICanvasJSDataSeriesOptions,
    ICanvasJSLegendEvent, ICanvasJSRangeChangingEvent,
    ICanvasJSToolTipContentFormatterEvent,
    StatisticsAvailableData, StatisticsData,
    StatisticsException,
    StatisticsResponse, SystemZoneOrder,
    TimePeriod
} from "../../models";
import {ParametersService} from "../parameters.service";
import {HttpClient} from "@angular/common/http";
import {StatisticsService} from "./statistics.service";
import {SubtitlesService} from "../subtitles.service";
import {ZonesSystemService} from "../zones-system.service";
import {DOCUMENT} from "@angular/common";

@Injectable({
    providedIn: 'root'
})
export class StatisticsTempService {
    columnsOccurrence: ColumnsOccurrence = {
        dynamic: [],
        any: false,
    };
    changeStatus: string = 'reset';
    axisType: string[] = [];
    axisTemplate: ICanvasJSAxisOptions;
    axisYObject: ICanvasJSAxisOptions[] = [];
    axisY2Object:  ICanvasJSAxisOptions[] = [];
    axisIndexes: ICanvasJSAxisIndex[] = [];

    constructor(
        private http: HttpClient,
        private parameters: ParametersService,
        private statistics: StatisticsService,
        private subtitles: SubtitlesService,
        private zonesSystem: ZonesSystemService,
        @Inject(DOCUMENT) private document: Document
    ) {
    }

    initialize(timePeriod: TimePeriod, selectedMonth: number, selectedYear: string) {
        this.getStatisticsTemperatureData(timePeriod, selectedMonth, selectedYear);
    }


    isDynamicEnergyYield(): boolean {
        return this.columnsOccurrence.dynamic.length > 0;
    }

    isColumnOccurrence(){
        return this.columnsOccurrence.any ;
    }

    clearColumnsOccurrence() {
        this.columnsOccurrence = {
            dynamic: [],
            any: false,
        };
    }

    private getStatisticsTemperatureData(timePeriod: TimePeriod, selectedMonth: number, selectedYear: string) {
        const baseUrl = `/api/v1/modules/${this.parameters.selectedModuleHash}`;

        this.http.get<StatisticsAvailableData>(`${baseUrl}/statistics/available`).subscribe(response => {
            this.columnsOccurrence = response.data;
        });

        const range = timePeriod === 'month'
            ? `/range/month/${selectedMonth}/year/${selectedYear}`
            : `/range/${timePeriod}`;
        const path = `${baseUrl}/statistics/linear${range}`;

        this.http.get<StatisticsResponse>(path).subscribe(response => {
            this.changeStatus = 'reset';
            const responseData = response.data;

            if (Object.keys(responseData.history).length > 0) {
                this.statistics.messageContent = '';
                responseData.descriptions = this.makeUniqueDescriptions(responseData.descriptions);

                responseData.descriptions.forEach((description) => {
                    if (responseData.history[description] !== undefined) {
                        responseData.history[description].forEach(singleXY => {
                            const singleDate = this.statistics.makeDate(singleXY.x as string);
                            singleXY.x = new Date(singleDate.year,
                                singleDate.month - 1,
                                singleDate.day,
                                singleDate.hour, singleDate.minute);
                        });
                    }
                });

                const chartData = this.getChartObject(responseData);
                this.statistics.statisticsChartReady.next(this.statistics.applyChartMode(chartData));
            } else {
                this.statistics.messageContent = this.subtitles.setSubtitle(1240);
                this.statistics.statisticsChartReady.next(this.statistics.applyChartMode({
                    backgroundColor: "#f8f8f8",
                }));
            }
        });
    }

    /**
     * Removes invalid duplicate descriptions from the controller data.
     * @param {string[]} descriptions - The array of descriptions ids.
     * @returns {string[]} A unique descriptions.
     */
    private makeUniqueDescriptions(descriptions: string[]): string[] {
        const descriptionsSet = new Set<string>();
        for (const description of descriptions) {
            descriptionsSet.add(description);
        }
        return Array.from(descriptionsSet);
    }

    private contains(array: any[], needle: any): boolean {
        const findNaN = needle !== needle;
        const indexOf = !findNaN && typeof Array.prototype.indexOf === 'function'
            ? Array.prototype.indexOf
            : (needle: any) => {
                for (let i = 0; i < array.length; i++) {
                    const item = array[i];
                    if ((findNaN && item !== item) || item === needle) {
                        return i;
                    }
                }
                return -1;
            };
        return indexOf.call(array, needle) > -1;
    }

    private getChartDescription(description: string): string {
        const zoneId = parseInt(description.substring(9), 10);
        return this.zonesSystem.getFullName(zoneId) || description;
    }

    private translateDescription(desc: string): string {
        if (desc.indexOf('1686') > -1) {
            const subtitleId = parseInt(desc.substring(5), 10);
            return this.subtitles.setSubtitle(subtitleId);
        } else if (desc.indexOf('|&^4fdkl|') > -1) {
            return this.parameters.isZonesSystemControllerType()
                ? this.getChartDescription(desc)
                : desc.substring(9);
        } else if (/^-?\d+$/.test(desc)) {
            const subtitleId = parseInt(desc, 10);
            return this.subtitles.setSubtitle(subtitleId) || desc;
        } else {
            return desc;
        }
    }

    private getZonesWithOrders(response: StatisticsData): [SystemZoneOrder[], string[]] {
        const unsortedZones: SystemZoneOrder[] = [];
        const otherDescriptions: string[] = [];

        response.descriptions.forEach(description => {
            const zoneId = parseInt(description.substring(9), 10);
            if (zoneId) {
                const zoneOrder = this.zonesSystem.getZonesOrder(zoneId)
                unsortedZones.push(zoneOrder);
            } else {
                otherDescriptions.push(description);
            }
        });

        return [unsortedZones, otherDescriptions];
    }

    private sortZonesByZoneIndex(zoneA: any, zoneB: any): number {
        return zoneA.zoneIndex - zoneB.zoneIndex;
    }

    private sortSystemZones(unsortedZones: SystemZoneOrder[]): SystemZoneOrder[][] {
        const containersInOrder: SystemZoneOrder[][] = [];
        unsortedZones.forEach(zoneOrder => {
            const index = zoneOrder.containerOrder - 1;
            if (index >= 0) {
                if (!Array.isArray(containersInOrder[index])) {
                    containersInOrder[index] = [];
                }
                containersInOrder[index].push(zoneOrder);
            }
        });

        return containersInOrder.map(zones => zones.sort(this.sortZonesByZoneIndex));
    }

    private mapZoneIdToDescription(zonesInOrder: SystemZoneOrder[][]): string[] {
        const flattenedZones = zonesInOrder.reduce((acc, val) => acc.concat(val), []);
        return flattenedZones.map((zoneOrder: any) => `|&^4fdkl|${zoneOrder.zoneId}`);
    }

    private sortDescriptionByZoneIndex(response: StatisticsData): StatisticsData {
        const [unsortedZones, otherDescriptions] = this.getZonesWithOrders(response);
        const zonesInOrder = this.sortSystemZones(unsortedZones);
        const zoneIdDescriptions = this.mapZoneIdToDescription(zonesInOrder);
        response.descriptions = [...zoneIdDescriptions, ...otherDescriptions];
        return response;
    }

    private prepareDataObject(response: StatisticsData): ICanvasJSDataSeriesOptions[] {
        const result: ICanvasJSDataSeriesOptions[] = [];
        let iterator = 0;

        if (this.parameters.isZonesSystemControllerType()) {
            response = this.sortDescriptionByZoneIndex(response);
        }

        this.axisIndexes = [];
        this.axisType = [];
        this.axisYObject = [];
        this.axisY2Object = [];
        this.axisIndexes.push({
            type: '0',
            axisYIndex: 0,
            axisY2Index: null,
            axisYType: 'primary',
            axisY2Type: null
        });
        this.prepareAxisY(response.exceptions, response.descriptions);

        response.descriptions.forEach(description => {
            const axisObject = this.axisIndexes.find(
                    element => element.type === response.exceptions.find(
                        exc => exc.subtitle_id === description)?.type)
                || this.axisIndexes[0];

            const obj: ICanvasJSDataSeriesOptions = {
                color: this.statistics.palette()[iterator],
                markerSize: 0,
                visible: iterator <= 8,
                connectNullData: true,
                nullDataLineDashType: "solid",
                type: "spline",
                name: this.translateDescription(description),
                showInLegend: true,
                markerType: "circle",
                dataPoints: response.history[description],
                legendMarkerType: 'circle',
                axisYIndex: '0',
                axisYType: 'primary',
            };

            if (axisObject.axisYIndex !== null) {
                obj.axisYIndex = axisObject.axisYIndex.toString();
                obj.axisYType = 'primary';
            } else if (axisObject.axisY2Index !== null) {
                obj.axisYIndex = axisObject.axisY2Index.toString();
                obj.axisYType = 'secondary';
            }

            result.push(obj);
            iterator++;
        });

        return result;
    }

    private prepareAxisY(exceptions: StatisticsException[], descriptions: string[]): void {
        this.axisType = [];
        this.axisTemplate = {
            margin: 10, labelFontSize: 15, includeZero: false,
            snapToDataPoint: true, lineThickness: 2,
            gridThickness: 0, lineColor: this.statistics.basicColor(),
            tickColor: this.statistics.basicColor(),
            crosshair: {
                labelMaxWidth: 100, labelWrap: false, enabled: true,
                color: "red", thickness: 1, lineDashType: "solid"
            }
        }
        this.axisYObject = [this.axisTemplate];
        this.axisY2Object = [];
        this.axisIndexes = [{
            type: '0', axisYIndex: 0, axisY2Index: null,
            axisYType: 'primary', axisY2Type: null
        }
        ];

        exceptions.forEach(item => {
            const tempAxis = _.cloneDeep(this.axisTemplate);
            const index = this.contains(this.axisType, item.type);

            if (index === false) {
                descriptions.forEach((descriptionItem, key) => {
                    if (item.subtitle_id === descriptionItem) {
                        tempAxis.lineColor = this.statistics.palette()[key];
                        tempAxis.tickColor = this.statistics.axisTickColor(tempAxis.lineColor);
                        if (key > 8) {
                            tempAxis.crosshair!.enabled = false;
                        }
                    }
                });

                if (this.axisYObject.length < this.axisY2Object.length) {
                    this.axisYObject.push(tempAxis);
                    this.axisIndexes.push({
                        type: item.type,
                        axisYIndex: this.axisYObject.length - 1,
                        axisY2Index: null,
                        axisYType: 'primary',
                        axisY2Type: null
                    });
                } else {
                    this.axisY2Object.push(tempAxis);
                    this.axisIndexes.push({
                        type: item.type,
                        axisYIndex: null,
                        axisY2Index: this.axisY2Object.length - 1,
                        axisYType: null,
                        axisY2Type: 'secondary'
                    });
                }

                this.axisType.push(item.type);
            }
        });
    }

    private pointsBubble(e: ICanvasJSToolTipContentFormatterEvent): string | null {
        let str = "";
        e.entries.forEach((element) => {
            if (element.dataSeries.visible) {
                const temp = `
                    <div style='width:10px;height:10px;border-radius: 5px;
                    display: inline-block;margin-right:5px;
                    background:${element.dataSeries.color}'></div>
                    ${element.dataSeries.name}: 
                    <strong>${element.dataPoint.y}</strong> <br/>
                `;
                str = str.concat(temp);
            }
        });

        return str !== '' ? str : null;
    }

    private getChartObject(response: StatisticsData): ICanvasJSChartOptions {
        this.prepareAxisY(response.exceptions, response.descriptions);
        let clicked = false;

        return {
            animationEnabled: this.document.location.port !== '4200',
            theme: 'dark1',
            height: 600,
            toolTip: {
                shared: true,
                contentFormatter: (e) => this.pointsBubble(e)
            },
            legend: {
                fontSize: 18,
                itemWidth: 300,
                symbolRadius: 0,
                fontFamily: "Roboto-Regular",
                cursor: "pointer",
                itemclick: (e: ICanvasJSLegendEvent) => {
                    if (!clicked) {
                        clicked = true;
                        const dataSeries = e.dataSeries;
                        dataSeries.visible = !(typeof dataSeries.visible === "undefined" || dataSeries.visible);

                        const axes = dataSeries.axisYType === 'secondary' ? e.chart.axisY2 : e.chart.axisY;
                        const axis = axes[Number(dataSeries.axisYIndex)];
                        let count = 0;
                        let color = dataSeries.color;

                        e.chart.data.forEach((singleData) => {
                            if (singleData.axisYType === dataSeries.axisYType
                                && singleData.axisYIndex === dataSeries.axisYIndex
                                && singleData.color !== dataSeries.color
                                && singleData.visible
                                && !dataSeries.visible) {
                                color = singleData.color;
                                count++;
                            }
                        });

                        if (axis) {
                            if (dataSeries.visible || count !== 0) {
                                if (!(dataSeries.axisYIndex === '0' && dataSeries.axisYType === 'primary')) {
                                    axis.set('lineColor', color);
                                    axis.set('tickColor', this.statistics.axisTickColor(color));
                                }

                                axis.set('lineThickness', 2);
                                axis.set('tickThickness', 2);
                                axis.set('crosshair', {
                                    labelMaxWidth: 100,
                                    labelWrap: false,
                                    enabled: true,
                                    color: "red",
                                    thickness: 1,
                                    lineDashType: "solid",
                                    labelBackgroundColor: this.statistics.basicColor(),
                                    labelFontColor: this.statistics.oppositeColor(),
                                });
                            } else {
                                axis.set('lineThickness', 0);
                                axis.set('tickThickness', 0);
                                axis.set('crosshair', {
                                    labelMaxWidth: 100,
                                    labelWrap: false,
                                    enabled: false,
                                    color: "red",
                                    thickness: 1,
                                    lineDashType: "solid",
                                    labelBackgroundColor: this.statistics.basicColor(),
                                    labelFontColor: this.statistics.oppositeColor(),
                                });
                            }
                        }

                        setTimeout(() => {
                            clicked = false;
                            e.chart.render();
                        }, 300);
                    }

                    e.chart.render();
                },
                itemmouseover: (e: ICanvasJSLegendEvent) => {
                    e.dataSeries.lineThickness = 4;
                    e.dataSeries.markerSize = 2;
                    e.chart.render();
                },
                itemmouseout: (e: ICanvasJSLegendEvent) => {
                    e.dataSeries.lineThickness = 2;
                    e.dataSeries.markerSize = 0;
                    e.chart.render();
                }
            },
            backgroundColor: this.statistics.background(),
            zoomEnabled: true,
            rangeChanging: (e: ICanvasJSRangeChangingEvent) => {
                if (e.trigger === 'zoom' && this.changeStatus === 'reset') {
                    e.chart.data.forEach((element) => {
                        element.set('markerSize', 5);
                    });

                    this.changeStatus = 'zoom';
                } else if (e.trigger === 'reset' && this.changeStatus === 'zoom') {
                    e.chart.data.forEach((element) => {
                        element.set('markerSize', 0);
                    });

                    this.changeStatus = 'reset';
                }
            },
            axisY: this.axisYObject,
            axisY2: this.axisY2Object,
            axisX: {
                xValueType: "dateTime",
                valueFormatString: "DD-MM HH:mm",
                labelFontSize: 13,
                lineThickness: 2,
                tickThickness: 1,
                tickLength: 5,
                crosshair: {
                    color: "red",
                    lineDashType: "solid",
                    enabled: true,
                    snapToDataPoint: true
                }
            },
            zoomType: "x",
            data: this.prepareDataObject(response)
        };
    }
}
